import React from "react";
import { ArrowRight, Sparkles, Clock } from "lucide-react";
import { TRIAL_URL } from "../constants";

/**
 * Main promotional block for the 7-day free trial.
 * Used in two placements (after Hero and before FinalCTA).
 * Visually distinct from the enrollment CTAs so it doesn't collide with them.
 */
export const TrialPromoBlock = ({ compact = false }) => (
  <section style={{ padding: compact ? "40px 24px" : "70px 24px" }}>
    <div
      className="fade-in"
      style={{
        maxWidth: 1240,
        margin: "0 auto",
        background: "linear-gradient(135deg, #1A1352 0%, #3F2DCB 55%, #5446EA 100%)",
        borderRadius: 32,
        padding: "clamp(32px, 5vw, 56px)",
        position: "relative",
        overflow: "hidden",
        color: "#fff",
        boxShadow: "0 30px 70px -25px rgba(26,19,82,0.4)",
      }}
    >
      {/* Decorative orange blob */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: -60,
          right: -60,
          width: 240,
          height: 240,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,79,26,0.35), rgba(255,79,26,0) 70%)",
          filter: "blur(10px)",
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: -80,
          left: -40,
          width: 220,
          height: 220,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(192,74,191,0.28), rgba(192,74,191,0) 70%)",
          filter: "blur(10px)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr",
          gap: 36,
          alignItems: "center",
        }}
        className="trial-grid"
      >
        <div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              padding: "6px 14px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 20,
            }}
          >
            <Sparkles size={13} color="#FFC56B" /> Promoción limitada · Sin pago
          </span>

          <h2
            className="h-display"
            style={{
              fontSize: "clamp(28px, 4vw, 46px)",
              color: "#fff",
              margin: "0 0 18px",
              lineHeight: 1.08,
            }}
          >
            ¡PRUEBA ACTIVEENGLISH GRATIS DURANTE{" "}
            <span
              style={{
                display: "inline-block",
                background: "#FF4F1A",
                color: "#fff",
                padding: "2px 12px",
                borderRadius: 10,
                boxShadow: "0 6px 0 0 #B8350E, 0 10px 20px -4px rgba(184,53,14,0.45)",
                whiteSpace: "nowrap",
              }}
            >
              7 DÍAS GRATIS
            </span>
            !
          </h2>

          <p
            style={{
              color: "rgba(255,255,255,0.9)",
              fontSize: 16,
              lineHeight: 1.6,
              marginBottom: 12,
              maxWidth: 560,
            }}
          >
            Descubre una nueva forma de aprender inglés a tu ritmo. Explora nuestra plataforma,
            practica con actividades interactivas y conoce nuestra metodología de aprendizaje.
          </p>
          <p
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 14,
              lineHeight: 1.6,
              marginBottom: 26,
              maxWidth: 520,
            }}
          >
            <strong style={{ color: "#fff" }}>Sin compromiso de matrícula.</strong>{" "}
            Tú decides si quieres continuar.
          </p>

          <a
            href={TRIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-orange"
            style={{ fontSize: 15, letterSpacing: "0.02em" }}
          >
            QUIERO MI SEMANA GRATIS <ArrowRight size={16} />
          </a>
        </div>

        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          className="trial-badge-wrap"
        >
          <div
            style={{
              background: "#fff",
              color: "#1A1352",
              borderRadius: 24,
              padding: "28px 32px",
              textAlign: "center",
              boxShadow: "0 20px 50px -15px rgba(0,0,0,0.3)",
              transform: "rotate(-3deg)",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(255,79,26,0.14)",
                color: "#FF4F1A",
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              <Clock size={12} /> Acceso inmediato
            </div>
            <div
              className="h-display"
              style={{ fontSize: 54, lineHeight: 1, color: "#1A1352" }}
            >
              7
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#1A1352",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                marginTop: 4,
              }}
            >
              días gratis
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#5F5A72",
                marginTop: 10,
                borderTop: "1px dashed rgba(26,19,82,0.15)",
                paddingTop: 10,
              }}
            >
              Sin tarjeta · Sin compromiso
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 820px) {
          .trial-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .trial-badge-wrap { padding: 0 !important; }
        }
      `}</style>
    </div>
  </section>
);

/**
 * Compact promotional strip. Used between the method section and the age section.
 */
export const TrialPromoStrip = () => (
  <section style={{ padding: "0 24px 20px" }}>
    <div
      className="fade-in"
      style={{
        maxWidth: 1240,
        margin: "0 auto",
        background: "#fff",
        border: "1.5px solid rgba(84,70,234,0.18)",
        borderRadius: 20,
        padding: "20px 26px",
        display: "flex",
        alignItems: "center",
        gap: 20,
        flexWrap: "wrap",
        boxShadow: "0 20px 50px -30px rgba(26,19,82,0.22)",
      }}
    >
      <span
        style={{
          width: 46,
          height: 46,
          borderRadius: 14,
          background: "linear-gradient(135deg,#5446EA,#C04ABF)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          flexShrink: 0,
          boxShadow: "0 6px 16px -4px rgba(84,70,234,0.5)",
        }}
      >
        <Sparkles size={20} />
      </span>
      <p
        style={{
          flex: "1 1 320px",
          color: "#1A1352",
          fontSize: 15,
          lineHeight: 1.55,
          margin: 0,
        }}
      >
        No te quedes con las dudas.{" "}
        <strong>Prueba nuestra plataforma durante 7 días</strong> y descubre cómo aprendes con
        ActiveEnglish.
      </p>
      <a
        href={TRIAL_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-orange"
        style={{ fontSize: 14, flexShrink: 0 }}
      >
        PROBAR GRATIS 7 DÍAS <ArrowRight size={15} />
      </a>
    </div>
  </section>
);

export default TrialPromoBlock;
